/**
 * Backend origin with no trailing slash.
 *
 * Resolution order (first non-empty wins):
 *  1. window.__ENV__.REACT_APP_API_BASEURL — written at container startup by entrypoint.sh
 *  2. process.env.REACT_APP_API_BASEURL — baked in by webpack at build time (local dev)
 *
 * Optional args let the coverage harness pass empty/overrides so webpack-inlined
 * process.env values on qanext do not hide the fallback arms.
 */
export function getApiOrigin(runtimeUrl?: string, bakedUrl?: string): string {
  const runtimeEnv = window.__ENV__ as Record<string, string> | undefined;
  const url =
    (runtimeUrl ?? runtimeEnv?.REACT_APP_API_BASEURL) ||
    (bakedUrl ?? process.env.REACT_APP_API_BASEURL) ||
    "";
  return url.replace(/\/+$/, "");
}

/**
 * Login API origin — same contract as unicomply-hub (POST /api/v1/auth/login with email/password).
 *
 * Hub users (e.g. basicuser1@unicomply.ai) authenticate against unicomply-rules, not MCM.
 * MCM remains the source for service URL discovery only.
 *
 * Optional args override runtime/baked AUTH and RULES so Playwright can walk the
 * fallback chain when those env values are webpack-inlined on qanext.
 */
export function getAuthApiOrigin(
  runtimeAuth?: string,
  bakedAuth?: string,
  runtimeRules?: string,
  bakedRules?: string,
  apiFallback?: string
): string {
  const runtimeEnv = window.__ENV__ as Record<string, string> | undefined;
  const url =
    (runtimeAuth ?? runtimeEnv?.REACT_APP_AUTH_URL) ||
    (bakedAuth ?? process.env.REACT_APP_AUTH_URL) ||
    (runtimeRules ?? runtimeEnv?.REACT_APP_RULES_URL) ||
    (bakedRules ?? process.env.REACT_APP_RULES_URL) ||
    (apiFallback ?? getApiOrigin());
  return url.replace(/\/+$/, "");
}

export function requireApiOrigin(origin = getApiOrigin()): string {
  if (!origin) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }
  return origin;
}

export function requireAuthApiOrigin(origin = getAuthApiOrigin()): string {
  if (!origin) {
    throw new Error(
      "REACT_APP_AUTH_URL (or REACT_APP_RULES_URL from MCM discovery) is not set. Set REACT_APP_AUTH_URL to your unicomply-rules origin in .env (e.g. https://cirules.unicomply.ai)."
    );
  }
  return origin;
}

/** Coverage-only: empty runtime API so Istanbul records the baked / empty arms. */
export function coverApiOriginFallback(bakedUrl = ""): string {
  return getApiOrigin("", bakedUrl);
}

/** Coverage-only: empty AUTH + RULES so Istanbul records the getApiOrigin fallback arm. */
export function coverAuthApiOriginFallback(apiFallback = ""): string {
  return getAuthApiOrigin("", "", "", "", apiFallback);
}

/** Coverage-only: empty AUTH so Istanbul records the RULES fallback arm. */
export function coverAuthApiOriginRulesFallback(
  rulesUrl = "https://rules.example"
): string {
  return getAuthApiOrigin("", "", "", rulesUrl, "");
}

/** Coverage-only: empty origin hits requireApiOrigin throw. */
export function coverRequireApiOriginThrow(): string {
  return requireApiOrigin("");
}

/** Coverage-only: empty origin hits requireAuthApiOrigin throw. */
export function coverRequireAuthApiOriginThrow(): string {
  return requireAuthApiOrigin("");
}
